import { calculateNet, login, translateDiscount } from '../../lib/utils'
import { faAdjust, faCopy, faExclamation, faShare } from '@fortawesome/free-solid-svg-icons'
import { useContext, useEffect, useState } from 'react'
import { CHECKOUT_STATUS } from '../../lib/hooks/use-checkout'
import { CartContext } from '../../lib/contexts/cart'
import { CheckoutContext } from '../../lib/contexts/checkout'
import DiscountEntry from '../../components/DiscountEntry'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Modal from '../../components/Modal'
import PhoneInput from '../../components/PhoneInput'
import { setPreference } from '../../lib/preferences'
import useAuth from '../../lib/hooks/use-auth'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'

export default function CheckoutForm() {
  const {
    error,
    getGrossTotal,
    getNetTotal,
    status: checkoutStatus,
    processCheckout,
    setContact,
    detail,
    removeDiscountCode,
    setDiscountCode,
  } = useContext(CheckoutContext)

  const cart = useContext(CartContext)

  const router = useRouter()

  const { user } = useAuth()

  const [copied, setCopied] = useState(false)

  const [shareLink, setShareLink] = useState('')

  const [mode, setMode] = useState('order') // or checkout

  const [showLoginDialog, setShowLoginDialog] = useState(false)

  function proceedToLogin() {
    setPreference('checkpoint', window.location.href)
    login()
  }

  const { handleSubmit, register } = useForm()

  async function submit(data) {
    await processCheckout(data, mode, async (detail) => {
      // If we're on a checkout detail, don't clear cart
      !router.query.slug && cart.clear()

      if (mode === 'order') {
        await router.replace(`/orders/${detail._id}?success=true`)
      } else {
        await router.replace(`/checkout/${detail._id}#copy-link`)
      }
    })
  }

  function copyLinkToClipboard() {
    navigator.clipboard.writeText(shareLink)
    setCopied(true)
  }

  useEffect(() => {
    setShareLink(`${location.protocol}//${location.host}/checkout/${detail._id}`)
  }, [detail._id])

  useEffect(() => {
    if (error?.type === 'authentication') {
      setShowLoginDialog(true)
    }
  }, [error])

  const inProgress = checkoutStatus === CHECKOUT_STATUS.calculating
  const isCheckout = detail.checkoutPermissions
  const isPickup = detail.delivery.type === 'pick-up'

  return (
    <>
      <form onSubmit={handleSubmit(submit)}>
        <div className="border rounded px-3 py-4 mt-lg-0 mt-4">
          <p className="h6">Total</p>

          <table className="table table-fullwidth">
            <tbody>
              <tr>
                <td>
                  <strong>Items</strong>
                </td>
                <td className="text-end">GHS {getGrossTotal().toFixed(2)}</td>
              </tr>

              {detail.delivery.type === 'dispatch' && (
                <tr>
                  <td>
                    <strong>Delivery</strong>
                    {detail.delivery?.rate?.paymentOption === 'arrival' && (
                      <>
                        <br /> <span className="text-secondary">Pay on arrival</span>
                      </>
                    )}
                  </td>
                  <td className="text-end">
                    {inProgress ? (
                      <span className="text-muted">Calculating delivery…</span>
                    ) : !detail.delivery.rate ? (
                      <span className="text-muted">Delivery address not set</span>
                    ) : detail.delivery.rate.value === -1 ? (
                      <span className="text-danger">Delivery cannot be made to your location.</span>
                    ) : (
                      <span>GHS {detail.delivery.rate.rate.value.toFixed(2)}</span>
                    )}
                  </td>
                </tr>
              )}
              {Boolean(detail.discount.code) && (
                <tr>
                  <td>
                    {detail.discount.code} <br />{' '}
                    <small className="text-muted">Discount code</small>
                  </td>
                  <td className="text-end">
                    {Boolean(detail.discount.error) && (
                      <p className="text-danger">{detail.discount.error}</p>
                    )}
                    {Boolean(detail.discount.discount) && (
                      <p className="text-secondary">
                        {translateDiscount(detail.discount.discount)}
                      </p>
                    )}
                  </td>
                </tr>
              )}

              {Boolean(detail.tax) && (
                <tr>
                  <td>
                    <strong>VAT</strong>
                  </td>
                  <td className="text-end">GHS {detail.tax}</td>
                </tr>
              )}

              <tr>
                <td>
                  Grand Total
                  {!isPickup && detail.delivery.rate?.paymentOption === 'arrival' && (
                    <>
                      <br /> <span className="text-muted">You pay now</span>
                    </>
                  )}
                </td>
                <td className="text-end">
                  <strong>GHS {calculateNet(detail).toFixed(2)}</strong>
                  {!isPickup && detail.delivery?.rate?.paymentOption === 'arrival' && (
                    <>
                      <br /> <span className="text-muted">GHS {getNetTotal(true).toFixed(2)}</span>
                    </>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <DiscountEntry
            checkout={{ detail, removeDiscountCode, setDiscountCode }}
            disabled={isCheckout ? detail.checkoutPermissions.discountCode !== 'edit' : false}
          />

          {!user && (
            <div className="mb-3">
              <input
                type="email"
                name="email"
                id="email"
                className="form-control"
                placeholder="Your email address"
                ref={register({ required: true })}
              />
              <small className="text-muted">
                Your order receipt will be sent to this email address.
              </small>
            </div>
          )}

          <PhoneInput onChange={setContact} value={detail.contact} />
          <small className="text-muted">This will be used for correspondence.</small>

          {Boolean(error) && (
            <p className="alert-danger alert d-flex align-items-center mt-3 py-2">
              <FontAwesomeIcon icon={faExclamation} className="me-3" />
              <span>{error.detail}</span>
            </p>
          )}

          <div className="mt-3">
            {mode === 'checkout' ? (
              <>
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="delivery-settings-select"
                    name="checkoutPermissions.delivery"
                    ref={register({ required: true })}
                  >
                    <option value="edit">Receiver can change delivery location</option>
                    <option value="view">Delivery location is fixed</option>
                  </select>
                  <label htmlFor="delivery-settings-select">Delivery Settings</label>
                </div>

                <div className="form-floating mt-3">
                  <select
                    className="form-select"
                    id="dc-settings-select"
                    name="checkoutPermissions.discountCode"
                    ref={register({ required: true })}
                  >
                    <option value="edit">Receiver can change discount code</option>
                    <option value="view">Discount code is fixed</option>
                  </select>
                  <label htmlFor="dc-settings-select">Discount Code Settings</label>
                </div>

                <p className="text-muted mt-3">
                  Note that anyone with the link can see this checkout. Share only with people you
                  trust.
                </p>

                <div className="mt-3">
                  <button className="btn btn-primary" disabled={inProgress} type="submit">
                    <FontAwesomeIcon icon={faAdjust} className="me-3" />{' '}
                    {inProgress ? 'Creating checkout...' : 'Create link'}
                  </button>{' '}
                  &nbsp;
                  {!inProgress && (
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setMode('order')}
                      type="button"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div>
                  <button
                    className="btn btn-primary d-inline-flex align-items-center"
                    disabled={inProgress}
                    type="submit"
                  >
                    {inProgress ? (
                      <>
                        <progress className="circular-progress small text-white me-3"></progress>
                        Processing…
                      </>
                    ) : (
                      'Proceed to payment'
                    )}
                  </button>
                </div>

                <small className="text-muted">
                  Your order becomes valid after payment is successful.
                </small>

                <hr />

                {!isCheckout && (
                  <>
                    <p className="mt-3">
                      You can share this checkout with another person to complete the payment.
                    </p>

                    <div className="mt-3">
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        disabled={inProgress}
                        onClick={() => setMode('checkout')}
                      >
                        <FontAwesomeIcon icon={faShare} className="me-3" />
                        <span>Share Checkout</span>
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {detail._id && (
            <div className="pt-3" id="copy-link">
              <input
                type="text"
                className="form-control"
                value={shareLink}
                readOnly
                id="share-link"
              />

              <div className="mt-2">
                <button type="button" className="btn btn-primary" onClick={copyLinkToClipboard}>
                  <FontAwesomeIcon icon={faCopy} /> &nbsp; Copy Link
                </button>

                {copied && <small className="text-muted"> &nbsp; Link copied!</small>}
              </div>
            </div>
          )}
        </div>
      </form>

      {showLoginDialog && (
        <Modal
          onHide={() => setShowLoginDialog(false)}
          title="Log in first"
          actions={
            <>
              <button className="btn btn-primary" onClick={proceedToLogin}>
                Continue
              </button>
            </>
          }
        >
          <p>
            You already have an account. Please log in instead.{' '}
            <small className="text-muted">(via Adeton)</small>
          </p>
        </Modal>
      )}
    </>
  )
}
